import type {
  DiscoverGeniallyIdResponse,
  GetPlayersLimitInfoQueryParams,
  GetPlayersLimitInfoResponse,
  IsUsernameInUseQueryParams,
  IsUsernameInUseResponse,
} from '@genially/p2p-lib';
import { createConstantRetryStrategy, withRetries } from '@genially/utils';

import { calculateServerIndex } from '../util/calculateServerIndex';
import { getP2pAxiosClient } from './utils/getP2pAxiosClient';

const discoverGeniallyId = async ({
  sessionId,
}: {
  sessionId: string;
}): Promise<DiscoverGeniallyIdResponse> => {
  return withRetries(
    async () => {
      return getP2pAxiosClient()
        .get(`/play/discover-genially-id?sessionId=${sessionId}`)
        .then(response => response.data);
    },
    createConstantRetryStrategy({ wait: 2500 }),
  );
};

const isUsernameInUse =
  (serverIndex: number) =>
  async ({
    geniallyId,
    sessionId,
    username,
  }: IsUsernameInUseQueryParams): Promise<IsUsernameInUseResponse> => {
    return withRetries(
      async () => {
        return getP2pAxiosClient(serverIndex)
          .get<IsUsernameInUseResponse>(
            `/play/is-username-in-use?geniallyId=${geniallyId}&sessionId=${sessionId}&username=${username}`,
          )
          .then(response => response.data);
      },
      createConstantRetryStrategy({ wait: 2500 }),
    );
  };

const getPlayersLimitInfo = async ({
  teamId,
}: GetPlayersLimitInfoQueryParams): Promise<GetPlayersLimitInfoResponse> => {
  return withRetries(
    async () => {
      return getP2pAxiosClient()
        .get<GetPlayersLimitInfoResponse>(`/play/players-limit?teamId=${teamId}`)
        .then(response => response.data);
    },
    createConstantRetryStrategy({ wait: 2500 }),
  );
};

export const PlayP2pRestClient = {
  discoverGeniallyId,
  withGeniallyId: ({ geniallyId }: { geniallyId: string }) => ({
    isUsernameInUse: async (params: Omit<IsUsernameInUseQueryParams, 'geniallyId'>) => {
      const serverIndex = await calculateServerIndex(geniallyId);

      return isUsernameInUse(serverIndex)({
        geniallyId,
        sessionId: params.sessionId,
        username: encodeURIComponent(params.username),
      });
    },
  }),
  getPlayersLimitInfo,
};
